import { useMemo } from "react";
import { recordedWorkMinutes } from "../workCalendar";
import type { BootstrapResponse } from "../types";

export type WorkerStatusRow = {
  /* 수량 계열(불출·회수·정상·불량·진행률)은 뺐다. 작업 탭의 불출 표가 건별 근거와 함께
     보여 주고, 정산 탭이 같은 것을 고른 기간·작업자로 보여 준다. 여기 남는 것은 그 둘이
     말하지 않는 돈 두 가지뿐이다. */
  /* 두 지표는 기간을 따르지 않는다 — 작업자 등급을 매기는 근거라 모든 프로젝트, 모든 기간을
     누적해서 본다. 시급으로 셈하는 사람은 같은 두 칸을 근무기록에서 채운다: 시급은 실제
     시급이고, 벌어들인 돈은 시급 × 근무시간이다. */
  hourlyWage: number; earned: number;
  partTimeDays: number; partTimeMinutes: number; partTimeLastWorkDate: string | null;
};

/** Lifetime issuance totals per worker (human or robot) — not scoped to any period filter. */
export function useWorkerStatusRows(data: BootstrapResponse): Map<string, WorkerStatusRow> {
  const unitPrices = useMemo(() => new Map(data.projects.flatMap((project) => project.subtasks.map((task) => [task.id, task.unitPrice]))), [data.projects]);
  const issuanceById = useMemo(() => new Map(data.issuances.map((issuance) => [issuance.id, issuance])), [data.issuances]);
  return useMemo(() => new Map(data.workers.map((worker) => {
    const issuances = data.issuances.filter((item) => item.workerId === worker.id);
    const earned = issuances.reduce((sum, item) => sum + item.normalQuantity * (unitPrices.get(item.subtaskId) ?? 0), 0);
    const timedItems = data.planRuns.flatMap((run) => run.items).filter((item) => item.workerId === worker.id && item.status === "completed" && item.workStartedAt && item.workCompletedAt);
    const actualMinutes = recordedWorkMinutes(timedItems);
    const timedEarning = timedItems.reduce((sum, item) => {
      const issuance = item.issuanceId ? issuanceById.get(item.issuanceId) : undefined;
      return sum + (issuance?.normalQuantity ?? 0) * (unitPrices.get(item.subtaskId) ?? 0);
    }, 0);
    const partTimeRecords = data.partTimeWorkRecords.filter((record) => record.workerId === worker.id);
    const partTimeMinutes = partTimeRecords.reduce((sum, record) => sum + record.workMinutes, 0);
    const partTimeEarned = partTimeRecords.reduce((sum, record) => sum + record.estimatedAmount, 0);
    const partTimeLastWorkDate = partTimeRecords.reduce<string | null>((latest, record) => !latest || record.workDate > latest ? record.workDate : latest, null);
    const hourly = worker.settlementType === "hourly";
    const row: WorkerStatusRow = {
      hourlyWage: hourly
        ? (partTimeMinutes ? Math.round(partTimeEarned / (partTimeMinutes / 60)) : 0)
        : (actualMinutes ? Math.round(timedEarning / (actualMinutes / 60)) : 0),
      earned: hourly ? partTimeEarned : earned,
      partTimeDays: partTimeRecords.length, partTimeMinutes, partTimeLastWorkDate
    };
    return [worker.id, row];
  })), [data.issuances, data.partTimeWorkRecords, data.planRuns, data.workers, issuanceById, unitPrices]);
}
